import { alpha, Button, Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import React, { forwardRef, RefAttributes } from "react";

interface Props {
    onClicked: () => void;
    icon: React.ReactNode;

    disabled?: boolean;
    testId?: string;

    noTabFocus?: boolean;
    className?: string;
    noBorder?: boolean;
    light?: boolean;
    size?: "tiny" | "small" | "medium" | "large";
}

const useStyles = makeStyles((theme: Theme) => ({
    button: {
        minWidth: 0,
        padding: "8px",
        borderRadius: "4px",
        transition: theme.transitions.create(["border", "color", "background-color"]),
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.primary.main,
        "&:hover": {
            backgroundColor: theme.palette.primary.light,
        },
        "&:disabled": {
            color: alpha(theme.palette.primary.contrastText, 0.54)
        }
    },
    buttonLight: {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.contrastText,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
        "&:hover": {
            backgroundColor: alpha(theme.palette.primary.contrastText, 0.8),
        },
        "&:disabled": {
            color: alpha(theme.palette.primary.main, 0.54)
        }
    },
    noBorder: {
        border: "none"
    },
    tiny: {
        padding: "2px"
    },
    small: {
        padding: "4px 6px"
    },
    large: {
        padding: "12px"
    }
}));

const QAIconButton: React.FC<Props & RefAttributes<HTMLButtonElement>> = forwardRef((props, ref) => {
    const classes = useStyles();
    return (
        <Button
            ref={ref}
            data-cy={props.testId}
            tabIndex={props.noTabFocus ? -1 : 0}
            className={clsx(
                classes.button,
                props.light && classes.buttonLight,
                props.noBorder && classes.noBorder,
                {
                    [classes.tiny]: props.size === "tiny",
                    [classes.small]: props.size === "small",
                    [classes.large]: props.size === "large"
                },
                props.className
            )}
            onClick={props.onClicked}
            size={props.size === "tiny" ? "small" : (props.size ?? "medium")}
            disabled={props.disabled}>
            {props.icon}
        </Button>
    );
});

export default QAIconButton;
